// v10/src/components/AnimatedSplash.js
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Volume2 } from 'lucide-react';
import HUDBackground from './HUDBackground';

const AUDIO_PATH             = '/cessna_trimmed_fadeout.mp3';
const MIN_SPLASH_DURATION_MS = 4500;

// ── Barra de onda ─────────────────────────────────────────────────
const WaveBars = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '3px', height: '20px' }}>
    {[0.6, 1, 0.75, 1, 0.5, 0.85, 0.65].map((h, i) => (
      <motion.div key={i}
        animate={{ scaleY: [h, 1, h * 0.4, 1, h] }}
        transition={{ duration: 0.7 + i * 0.08, repeat: Infinity, ease: 'easeInOut', delay: i * 0.1 }}
        style={{ width: '3px', height: '20px', borderRadius: '2px',
          background: 'linear-gradient(to top, #00d4ff, #0088aa)', transformOrigin: 'bottom' }}
      />
    ))}
  </div>
);

// ── Motor Radial SVG ──────────────────────────────────────────────
const RadialEngine = ({ propAngle, propBlur, propOpacity }) => {
  const cx = 160, cy = 158;

  const Cylinder = ({ rotation }) => (
    <g transform={`rotate(${rotation}, ${cx}, ${cy})`}>
      <rect x="148" y="53" width="24" height="52" rx="5"
        fill="url(#cylGrad)" stroke="#2d4a5e" strokeWidth="1"/>
      <rect x="150" y="55" width="20" height="8" rx="2"
        fill="#a0bcd0" stroke="#2d4a5e" strokeWidth="0.6"/>
      {[67,72,77,82,87,92,97].map(y => (
        <line key={y} x1="150" y1={y} x2="170" y2={y}
          stroke="#3a5a70" strokeWidth="0.7"/>
      ))}
    </g>
  );

  return (
    <svg viewBox="0 0 320 316" xmlns="http://www.w3.org/2000/svg"
      style={{ width: '100%', height: '100%', overflow: 'visible' }}>
      <defs>
        <radialGradient id="cylGrad" cx="40%" cy="30%" r="65%">
          <stop offset="0%"   stopColor="#c8dae8"/>
          <stop offset="60%"  stopColor="#8aafc8"/>
          <stop offset="100%" stopColor="#4a6a82"/>
        </radialGradient>
        <radialGradient id="crankGrad" cx="40%" cy="35%" r="65%">
          <stop offset="0%"   stopColor="#b0c8dc"/>
          <stop offset="100%" stopColor="#3a5a70"/>
        </radialGradient>
        <radialGradient id="spinnerGrad" cx="38%" cy="32%" r="65%">
          <stop offset="0%"   stopColor="#d0e2ee"/>
          <stop offset="100%" stopColor="#2a4a60"/>
        </radialGradient>
        <linearGradient id="bladeGradA" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"   stopColor="#c0d4e4"/>
          <stop offset="50%"  stopColor="#8aafc8"/>
          <stop offset="100%" stopColor="#3a5a70"/>
        </linearGradient>
        <linearGradient id="bladeGradB" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"   stopColor="#3a5a70"/>
          <stop offset="50%"  stopColor="#8aafc8"/>
          <stop offset="100%" stopColor="#c0d4e4"/>
        </linearGradient>
        <filter id="engineShadow" x="-15%" y="-15%" width="130%" height="130%">
          <feDropShadow dx="2" dy="3" stdDeviation="4" floodColor="#00000055"/>
        </filter>
      </defs>

      {/* 7 cilindros radiales */}
      {[0, 51, 103, 154, 206, 257, 309].map(rot => (
        <Cylinder key={rot} rotation={rot} />
      ))}

      {/* Crankcase */}
      <circle cx={cx} cy={cy} r="60"
        fill="url(#crankGrad)" stroke="#2d4a5e" strokeWidth="1.8"
        filter="url(#engineShadow)"/>
      <circle cx={cx} cy={cy} r="54"
        fill="none" stroke="#4a6a82" strokeWidth="0.6" opacity="0.5"/>
      <circle cx={cx} cy={cy} r="46"
        fill="#3a5870" stroke="#2d4a5e" strokeWidth="1" opacity="0.6"/>

      {/* Hélice animada */}
      <g
        transform={`rotate(${propAngle}, ${cx}, ${cy})`}
        style={{
          filter: propBlur > 0.3 ? `blur(${propBlur.toFixed(1)}px)` : undefined,
          opacity: propOpacity,
        }}
      >
        {/* Pala izquierda */}
        <path d={`M148,155 C140,148 118,128 88,104 C70,90 52,80 38,78
          C32,77 28,79 27,83 C26,88 30,94 40,98
          C55,104 76,110 100,124 C124,138 142,150 150,157 Z`}
          fill="url(#bladeGradA)" stroke="#2d4a5e" strokeWidth="1.1"/>
        <path d="M148,155 C140,148 118,128 88,104 C70,90 52,80 38,78"
          fill="none" stroke="#d8eaf6" strokeWidth="0.7" opacity="0.6"/>
        {/* Pala derecha */}
        <path d={`M172,161 C180,168 202,188 232,212
          C250,226 268,236 282,238 C288,239 292,237 293,233
          C294,228 290,222 280,218 C265,212 244,206 220,192
          C196,178 178,166 170,159 Z`}
          fill="url(#bladeGradB)" stroke="#2d4a5e" strokeWidth="1.1"/>
        <path d="M172,161 C180,168 202,188 232,212 C250,226 268,236 282,238"
          fill="none" stroke="#d8eaf6" strokeWidth="0.7" opacity="0.5"/>
      </g>

      {/* Spinner — siempre encima */}
      <circle cx={cx} cy={cy} r="26"
        fill="url(#spinnerGrad)" stroke="#2d4a5e" strokeWidth="1.5"
        filter="url(#engineShadow)"/>
      <circle cx={cx} cy={cy} r="18"
        fill="#2a4a60" stroke="#3a5a70" strokeWidth="1"/>
      <circle cx={cx} cy={cy} r="10"
        fill="#1a3045" stroke="#4a6a80" strokeWidth="0.8"/>
      <circle cx={cx} cy={cy} r="5"
        fill="#7a9ab0" stroke="#2d4a5e" strokeWidth="0.8"/>
      <line x1={cx-3} y1={cy} x2={cx+3} y2={cy} stroke="#2d4a5e" strokeWidth="1.2"/>
      <line x1={cx} y1={cy-3} x2={cx} y2={cy+3} stroke="#2d4a5e" strokeWidth="1.2"/>

      {/* Texto técnico */}
      <g fontFamily="'Share Tech Mono', monospace" fill="#5a8090" opacity="0.5">
        <text x="160" y="14"  fontSize="7.5" textAnchor="middle" letterSpacing="2">MOTOR RADIAL — 7 CIL.</text>
        <text x="160" y="24"  fontSize="6"   textAnchor="middle" letterSpacing="1.5">CONTINENTAL W670 REF.</text>
        <line x1="82"  y1="272" x2="238" y2="272" stroke="#3a5a70" strokeWidth="0.5" opacity="0.5"/>
        <line x1="82"  y1="269" x2="82"  y2="275" stroke="#3a5a70" strokeWidth="0.5" opacity="0.5"/>
        <line x1="238" y1="269" x2="238" y2="275" stroke="#3a5a70" strokeWidth="0.5" opacity="0.5"/>
        <text x="160" y="284" fontSize="6" textAnchor="middle" letterSpacing="1">Ø 1.07 M  —  HÉL. 2.20 M</text>
      </g>
    </svg>
  );
};

// ── Componente principal ──────────────────────────────────────────
const AnimatedSplash = ({ onComplete }) => {
  const audioRef     = useRef(null);
  const animRef      = useRef(null);
  const angleRef     = useRef(0);

  const [hasStarted,  setHasStarted]  = useState(false);
  const [isPlaying,   setIsPlaying]   = useState(false);
  const [propAngle,   setPropAngle]   = useState(0);
  const [propBlur,    setPropBlur]    = useState(0);
  const [propOpacity, setPropOpacity] = useState(1);

  const TARGET_RPM = 600;
  const ANIM_TIME  = 4000; // ms para alcanzar idle

  // ── Fade out audio ────────────────────────────────────────────
  const fadeOutAudio = (duration = 900) => {
    const audio = audioRef.current;
    if (!audio || audio.paused) return;
    const steps     = 30;
    const interval  = duration / steps;
    const decrement = audio.volume / steps;
    const fade = setInterval(() => {
      if (audio.volume > decrement) {
        audio.volume = Math.max(0, parseFloat((audio.volume - decrement).toFixed(4)));
      } else {
        audio.volume = 0;
        audio.pause();
        clearInterval(fade);
      }
    }, interval);
  };

  // ── Animación hélice: cranking → aceleración → idle ───────────
  const animatePropeller = (startTime) => {
    const elapsed  = performance.now() - startTime;
    const progress = Math.min(elapsed / ANIM_TIME, 1);

    let rpmFactor;
    if (progress < 0.15) {
      // Cranking irregular
      rpmFactor = progress * 0.3 + Math.sin(elapsed * 0.018) * 0.04 * (1 - progress / 0.15);
    } else if (progress < 0.6) {
      // Aceleración easeIn
      const p = (progress - 0.15) / 0.45;
      rpmFactor = 0.045 + p * p * 0.7;
    } else {
      // Estabilización idle con micro-oscilación
      const p = (progress - 0.6) / 0.4;
      rpmFactor = 0.745 + p * 0.255 + Math.sin(elapsed * 0.004) * 0.008 * (1 - p);
    }

    const rpm         = TARGET_RPM * rpmFactor;
    const degPerFrame = (rpm / 60) * 360 / 60;
    angleRef.current  = (angleRef.current + degPerFrame) % 360;

    const blurAmt    = Math.max(0, (rpm - 150) / 450) * 3.5;
    const opacityVal = Math.max(0.35, 1 - (rpm / TARGET_RPM) * 0.55);

    setPropAngle(angleRef.current);
    setPropBlur(blurAmt);
    setPropOpacity(opacityVal);

    if (progress < 1) {
      animRef.current = requestAnimationFrame(() => animatePropeller(startTime));
    } else {
      idleLoop();
    }
  };

  const idleLoop = () => {
    const idleRPM     = TARGET_RPM + Math.sin(performance.now() * 0.001) * 15;
    const degPerFrame = (idleRPM / 60) * 360 / 60;
    angleRef.current  = (angleRef.current + degPerFrame) % 360;
    setPropAngle(angleRef.current);
    setPropBlur(3.2);
    setPropOpacity(0.38);
    animRef.current = requestAnimationFrame(idleLoop);
  };

  // ── Click botón ───────────────────────────────────────────────
  const handleStart = () => {
    setHasStarted(true);

    // Audio con fade in suave
    if (audioRef.current) {
      audioRef.current.volume = 0;
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          let vol = 0;
          const fi = setInterval(() => {
            vol = Math.min(vol + 0.05, 1);
            if (audioRef.current) audioRef.current.volume = vol;
            if (vol >= 1) clearInterval(fi);
          }, 50);
        })
        .catch(() => setIsPlaying(true));
    }

    // Animación hélice
    const t0 = performance.now();
    animRef.current = requestAnimationFrame(() => animatePropeller(t0));

    // Completar splash
    setTimeout(() => fadeOutAudio(1200), MIN_SPLASH_DURATION_MS - 1200);
    setTimeout(() => onComplete(), MIN_SPLASH_DURATION_MS + 400);    
  };

  // Cleanup
  useEffect(() => () => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 1.02 }} 
      transition={{ duration: 1.2, ease: 'easeInOut' }} 
      style={{
        position: 'fixed', inset: 0, zIndex: 50,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden',
        background: 'linear-gradient(160deg, #060d1f 0%, #0a1628 40%, #0d0a24 100%)',
      }}
    >
      <audio ref={audioRef} src={AUDIO_PATH} preload="auto" />
      <HUDBackground />

      {/* Halo pulsante */}
      <motion.div
        animate={{ scale: [1, 1.1, 1], opacity: [0.2, 0.4, 0.2] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          position: 'absolute', width: '500px', height: '500px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(74,106,130,0.3), rgba(37,99,235,0.08), transparent 70%)',
          filter: 'blur(50px)', pointerEvents: 'none', zIndex: 1,
        }}
      />

      {/* Motor — entra desde arriba */}
      <motion.div
        initial={{ y: -60, opacity: 0, scale: 0.88 }}
        animate={{ y: 0,   opacity: 1, scale: 1 }}
        transition={{ type: 'spring', stiffness: 65, damping: 14, delay: 0.2 }}
        style={{ position: 'relative', zIndex: 2, width: 'min(80vw, 300px)', height: 'min(80vw, 300px)' }}
      >
        {/* Glow detrás */}
        <motion.div
          animate={{ opacity: isPlaying ? [0.6, 0.9, 0.6] : [0.25, 0.45, 0.25], scale: isPlaying ? [1, 1.06, 1] : 1 }}
          transition={{ duration: isPlaying ? 1.5 : 3, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            position: 'absolute', inset: '-20px', borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(74,106,130,0.45), rgba(42,74,96,0.2), transparent 70%)',
            filter: 'blur(16px)',
          }}
        />
        {/* Scan line HUD */}
        <motion.div
          animate={{ top: ['0%', '100%', '0%'], opacity: [0, 0.3, 0.3, 0] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: 'linear', repeatDelay: 2 }}
          style={{
            position: 'absolute', left: 0, right: 0, height: '2px',
            background: 'linear-gradient(90deg, transparent, rgba(0,212,255,0.5), transparent)',
            zIndex: 4, pointerEvents: 'none',
          }}
        />
        <RadialEngine propAngle={propAngle} propBlur={propBlur} propOpacity={propOpacity} />
      </motion.div>

      {/* Título */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7, duration: 0.6 }}
        style={{ position: 'relative', zIndex: 2, marginTop: '10px', textAlign: 'center' }}
      >
        <div style={{
          fontFamily: "'Orbitron', 'Share Tech Mono', monospace",
          fontSize: 'clamp(26px, 8vw, 42px)',
          fontWeight: 900, letterSpacing: '-1px', lineHeight: 1,
          color: '#e8f4ff',
          textShadow: '0 0 40px rgba(0,212,255,0.15)',
        }}>
          CONDOR<span style={{ color: '#00d4ff', textShadow: '0 0 20px #00d4ff88' }}>360</span>
        </div>
        <div style={{
          fontFamily: "'Share Tech Mono', monospace",
          fontSize: '9px', letterSpacing: '0.3em', color: '#00d4ff',
          opacity: 0.55, marginTop: '5px', textTransform: 'uppercase',
        }}>
          Gestión Aeronáutica
        </div>
      </motion.div>

      {/* Separador */}
      <motion.div
        initial={{ width: 0, opacity: 0 }}
        animate={{ width: '220px', opacity: 1 }}
        transition={{ delay: 1.0, duration: 0.6 }}
        style={{ height: '1px', background: 'linear-gradient(90deg, transparent, #00d4ff44, transparent)', margin: '14px 0 12px', zIndex: 2 }}
      />

      {/* Botón / estado */}
      <div style={{ position: 'relative', zIndex: 2 }}>
        <AnimatePresence mode="wait">
          {!hasStarted ? (
            <motion.div key="btn"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10, scale: 0.9 }}
              transition={{ delay: 0.9, type: 'spring', stiffness: 140 }}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}
            >
              <motion.button
                onClick={handleStart}
                whileHover={{ scale: 1.04, y: -2 }}
                whileTap={{ scale: 0.96 }}
                style={{
                  position: 'relative', overflow: 'hidden',
                  display: 'flex', alignItems: 'center', gap: '13px',
                  padding: '0 26px', height: '52px',
                  background: 'rgba(10,15,30,0.8)',
                  border: '1px solid rgba(0,212,255,0.3)',
                  borderRadius: '4px', cursor: 'pointer',
                  backdropFilter: 'blur(16px)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                  fontFamily: "'Share Tech Mono', monospace",
                }}
              >
                {/* Shimmer */}
                <motion.div
                  animate={{ x: ['-150%', '250%'] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: 'linear', repeatDelay: 1 }}
                  style={{
                    position: 'absolute', top: 0, bottom: 0, width: '35%',
                    background: 'linear-gradient(90deg, transparent, rgba(0,212,255,0.1), transparent)',
                    pointerEvents: 'none',
                  }}
                />
                {/* Play icon */}
                <motion.div
                  animate={{ scale: [1, 1.1, 1], boxShadow: ['0 0 0 0 rgba(0,212,255,0.5)', '0 0 0 8px rgba(0,212,255,0)', '0 0 0 0 rgba(0,212,255,0)'] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
                  style={{
                    width: '32px', height: '32px', borderRadius: '50%', background: '#00d4ff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}
                >
                  <div style={{ width: 0, height: 0, borderTop: '6px solid transparent', borderBottom: '6px solid transparent', borderLeft: '10px solid #040c18', marginLeft: '2px' }} />
                </motion.div>
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#e8f4ff', letterSpacing: '0.12em' }}>
                  INICIAR CONDOR360
                </span>
                <Volume2 style={{ width: '14px', height: '14px', color: 'rgba(0,212,255,0.5)', flexShrink: 0 }} />
              </motion.button>
              <motion.p
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}
                style={{ fontSize: '9px', color: 'rgba(255,255,255,0.22)', letterSpacing: '0.22em', fontFamily: "'Share Tech Mono', monospace", textTransform: 'uppercase', margin: 0 }}
              >
                ▸ activá el audio antes de continuar
              </motion.p>
            </motion.div>
          ) : (
            <motion.div key="loading"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <motion.div animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 1.5, repeat: Infinity }}>
                  <Volume2 style={{ width: '15px', height: '15px', color: '#00d4ff' }} />
                </motion.div>
                <WaveBars />
              </div>
              <motion.p
                animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Infinity }}
                style={{ margin: 0, fontSize: '9px', fontWeight: 700, color: 'rgba(0,212,255,0.7)', letterSpacing: '0.25em', textTransform: 'uppercase', fontFamily: "'Share Tech Mono', monospace" }}
              >
                Motor en idle — listo para volar
              </motion.p>
              <div style={{ width: '210px', height: '2px', borderRadius: '2px', background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
                <motion.div
                  initial={{ width: '0%' }}
                  animate={{ width: '100%' }}
                  transition={{ duration: MIN_SPLASH_DURATION_MS / 1000, ease: 'linear' }}
                  style={{ height: '100%', borderRadius: '2px', background: 'linear-gradient(90deg, #00d4ff, #0088aa)' }}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Barra de carga inferior */}
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 2, ease: [0.4, 0, 0.2, 1], delay: 0.3 }}
        style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          height: '2px', transformOrigin: 'left',
          background: 'linear-gradient(90deg, #00d4ff, #0088aa)',
          boxShadow: '0 0 8px #00d4ff',
        }}
      />
    </motion.div>
  );
};

export default AnimatedSplash;
